import React, {
  useState,
  useEffect,
  useContext,
  Suspense,
  lazy,
  useRef,
} from "react";
import { Logger } from "@aws-amplify/core";
import API, { graphqlOperation } from "@aws-amplify/api";
import Auth from "@aws-amplify/auth";
import { StoreContext } from "./context/StoreContext";
import { Router, Route, withRouter, Redirect } from "react-router-dom";
import { createBrowserHistory } from "history";
import "./App.css";
import { Helmet } from "react-helmet";
import {
  publicsiteByCompany,
  companyBySubdomain,
  companyLocationByCompany,
} from "./graphql/queries";
import {
  servicesOfCompany,
  skillByCompany,
} from "./queries/ListBookingsQueries";
import { parse } from "query-string";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import { createTheme } from "@mui/material/styles";
import SignIn from "./auth/SignIn";
import SignUp from "./auth/SignUp";
import Logout from "./auth/Logout";
import { TipPage } from "./components/tip/TipPage";
import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";
import { Typography } from "@mui/material";
import mbLogo from "../src/images/MBLogo.svg";
import "typeface-roboto";
import "typeface-raleway";
import { buildMainTheme } from "./styles/PublicSiteStyles";
import Forgot from "./auth/Forgot";
import { fetchGTMIds, initializeGTM } from "./modules/GoogleTagManager";
import {
  maybeRedirectToV2,
  isAbHost,
  setAbSignedInIdentity,
  attachCachedAbIdentity,
} from "./modules/AbTest";
import { getClientIfExists } from "./modules/BookingService";
import i18n from "./i18n/config";
import { checkCompanyHasTosFile } from "./utils/Common/Template";
import MaintenanceDialog from "./components/MaintenanceDialog";
import {
  MB_SERVICE_CLASSIFICATION_GROUP,
  SENTRY_POPUP_ENV,
} from "./utils/Constants";
import { useAtom } from "jotai";
import {
  allActiveServicesAtom,
  allCompanyLocationsAtom,
  allServicesAtom,
  courseDatesAtom,
  courseDatesQueryParamsAtom,
} from "./atoms/atoms";
import { MB_INFO_COLOR_MAIN } from "./utils/Constants";
import { fetchCourseDates } from "./modules/ScheduleService";
Sentry.init({
  dsn: "https://058cfe0ab69349feb230ca4b204f17e4@o435799.ingest.sentry.io/5428728",
  beforeSend(event, hint) {
    // Check if it is an exception, and if so, show the report dialog
    if (
      process.env.REACT_APP_SENTRY_ENVT === SENTRY_POPUP_ENV &&
      event.exception
    ) {
      Sentry.showReportDialog({
        eventId: event.event_id,
        labelComments:
          "What happened? Please tell us the steps that caused the issue.",
        subtitle2:
          "Please tell us the steps that you took that caused the issue. This will help us fix the problem. Thank you.",
      });
    }
    return event;
  },
  environment: process.env.REACT_APP_SENTRY_ENVT,
  integrations: [new Integrations.BrowserTracing()],
  release: "marketbox-public-site@" + process.env.npm_package_version,
  tracesSampleRate: 1.0, // lower this in prod between 0 to 1
});

const SelectService = lazy(() =>
  import("./components/booking/SelectService").catch((e) =>
    window.location.reload(),
  ),
);
const ProviderPermalink = lazy(() =>
  import("./components/ProviderPermalink").catch((e) =>
    window.location.reload(),
  ),
);
const ProviderDirectory = lazy(() =>
  import("./components/booking/ProviderDirectory").catch((e) =>
    window.location.reload(),
  ),
);
const Provider = lazy(() =>
  import("./components/booking/Provider").catch((e) =>
    window.location.reload(),
  ),
);

const ChooseTimeslot = lazy(() =>
  import("./components/booking/ChooseTimeslot").catch((e) =>
    window.location.reload(),
  ),
);
const AutoSelectProviderTimeSlot = lazy(() =>
  import("./components/booking/AutoSelectProviderTimeSlot").catch((e) =>
    window.location.reload(),
  ),
);
const ConfirmAppointmentDetail = lazy(() =>
  import("./components/booking/ConfirmAppointmentDetails").catch((e) =>
    window.location.reload(),
  ),
);
const OrderPayment = lazy(() =>
  import("./components/booking/OrderPayment").catch((e) =>
    window.location.reload(),
  ),
);
const ConfirmOrder = lazy(() =>
  import("./components/booking/ConfirmOrder").catch((e) =>
    window.location.reload(),
  ),
);
const ChoosePackage = lazy(() =>
  import("./components/booking/ChoosePackage").catch((e) =>
    window.location.reload(),
  ),
);
const BookingComplete = lazy(() =>
  import("./components/booking/BookingComplete").catch((e) =>
    window.location.reload(),
  ),
);
const NotFound = lazy(() =>
  import("./components/NotFound").catch((e) => window.location.reload()),
);
const PageNotFound = lazy(() =>
  import("./components/PageNotFound").catch((e) => window.location.reload()),
);
const MsgTest = lazy(() =>
  import("./components/booking/MsgTest").catch((e) => window.location.reload()),
);
const ContactPage = lazy(() =>
  import("./components/booking/ContactPage").catch((e) =>
    window.location.reload(),
  ),
);
const logger = new Logger("App");
let params;
const limit = process.env.REACT_APP_LISTLIMIT; // global setting for list limits

// set default theme
const defaultButtonColor = "#ff4400";
const defaultButtonTextColor = "white";
let buttonColor = localStorage.getItem("buttonColor") || defaultButtonColor;
let buttonTextColor =
  localStorage.getItem("buttonTextColor") || defaultButtonTextColor;

let theme = createTheme({
  palette: {
    primary: {
      main: buttonColor,
    },
  },
});

export const history = createBrowserHistory();
export const UserContext = React.createContext();

function App(props) {
  const [user, setUser] = useState();
  const [guestUser, setGuestUser] = useState();
  const [guestCheckout, setGuestCheckout] = useState(false);
  const [userAttributes, setAttributes] = useState({});
  const [companyId, setCompanyId] = useState("");
  const [company, setCompany] = useState();
  const [companyLocations, setCompanyLocations] = useState();
  const [skills, setSkills] = useState();
  // const [courseDates, setCourseDates] = useState();
  const [publicSiteSettings, setPublicSiteSettings] = useState();
  const { state, actions } = useContext(StoreContext);
  const [serviceLoading, setServiceLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [landingPage, setLandingPage] = useState(false);
  const [loggedOut, setLoggedOut] = useState(false);
  const currentPath = "/";
  const [showLoader, setShowLoader] = useState(true);
  const [selectedColor, setSelectedColor] = useState(null);
  const [provAgnoFlow, setProvAgnoFlow] = useState(false);
  const [defaultCountry, setDefaultCountry] = useState();
  let pathingInfo = {
    currentPathname: null,
    currentSearch: null,
    message: "Please use the Previous and Next buttons to navigate.",
    openMessage: false,
  };
  const [allServices, setAllServices] = useAtom(allServicesAtom);
  const [allActiveServices] = useAtom(allActiveServicesAtom);
  const [, setAllCompanyLocations] = useAtom(allCompanyLocationsAtom);
  const [, setCourseDates] = useAtom(courseDatesAtom);
  const [, setCourseDatesQueryParams] = useAtom(courseDatesQueryParamsAtom);
  // return new theme, set primary color only for now
  const fetchSkillsData = async (compId) => {
    const response = await API.graphql(
      graphqlOperation(skillByCompany, {
        companyId: compId,
        filter: {
          and: [{ active: { ne: false } }, { deleted: { ne: true } }],
        },
        limit,
      }),
    );
    const result = await response.data.skillByCompany.items;
    setSkills(result);
  };

  const fetchServiceTypeData = async (
    compId,
    multipleServices,
    isProviderAgnostic,
  ) => {
    const filter = {
      and: [
        { active: { ne: false } },
        { deleted: { ne: true } },
        { isVisible: { ne: false } },
      ],
    };
    const response = await API.graphql(
      graphqlOperation(servicesOfCompany, {
        companyId: compId,
        filter,
        limit,
      }),
    );
    const result = await response.data.serviceTypeByCompany.items?.filter(
      (service) => {
        if (
          (multipleServices || isProviderAgnostic) &&
          service.Classification === MB_SERVICE_CLASSIFICATION_GROUP
        )
          return false;
        return true;
      },
    );

    setAllServices(sortByOrdinal(result));
    setServiceLoading(false);
  };

  const fetchCompanyLocations = async (companyId, offersRemoteServices) => {
    const filter = {
      and: [{ active: { ne: false } }, { deleted: { ne: true } }],
    };

    const response = await API.graphql(
      graphqlOperation(companyLocationByCompany, {
        companyId,
        filter,
        limit,
      }),
    );
    let result = await response.data.companyLocationByCompany.items;
    // add "My Address to the beginning of the array"
    if (offersRemoteServices) {
      result.unshift({
        id: "0",
        locationname: "At your address",
      });
    }
    setCompanyLocations(result);
    setAllCompanyLocations(result);
  };

  const fetchCompanyPublicSettings = async (compId) => {
    let primaryColor = "";
    const response = await API.graphql(
      graphqlOperation(publicsiteByCompany, { companyId: compId }),
    );
    let [settings] = response.data.publicsiteByCompany.items;
    let styleCustomization;
    if (
      settings?.StyleCustomization &&
      settings.StyleCustomization.length > 0
    ) {
      styleCustomization = JSON.parse(settings.StyleCustomization);
      setPublicSiteSettings({
        ...settings,
        StyleCustomization: styleCustomization,
      });
    } else {
      styleCustomization = {
        bookingFlowCustomizations: {
          showHeader: true,
          backgroundColor:
            publicSiteSettings?.buttonColor || MB_INFO_COLOR_MAIN,
          opacity: 0.2,
          circularLogo: false,
          showHeadings: true,
          showLogo: true,
          fontTypeForAllHeading: "Raleway",
          servicesDisplay: "CARD",
          mainHeading: company?.name
            ? company.name
            : "Please set Heading 1 in company public settings",
          mandatoryNotes: false,
          zeroBookingsPackage: false,
          checkoutOption: "ACCOUNT_OR_GUEST",
          hidePhysicalLocationAddress: false,
        },
      };
      setPublicSiteSettings({
        ...settings,
        StyleCustomization: styleCustomization,
      });
    }

    localStorage.setItem("backgroundImgUrl", settings.backgroundImageUrl);
    localStorage.setItem("buttonColor", settings.buttonColor);
    localStorage.setItem("heading1Text", settings.heading1Text);
    localStorage.setItem("heading2Text", settings.heading2Text);
    localStorage.setItem("textColor", settings.textColor);
    localStorage.setItem(
      "buttonTextColor",
      styleCustomization?.bookingFlowCustomizations?.buttonTextColor ||
        defaultButtonTextColor,
    );

    if (
      styleCustomization?.tipPageCustomizations?.showTipOption !== undefined
    ) {
      localStorage.setItem(
        "showTipOption",
        styleCustomization?.tipPageCustomizations.showTipOption,
      );
    }
    theme = buildMainTheme(
      settings.buttonColor.trim(),
      buttonTextColor,
      settings?.textColor?.trim(),
    );
    setSelectedColor(settings.buttonColor.trim());
    primaryColor = settings.buttonColor;
    primaryColor = primaryColor.trim(); // trim in case space added.
    setSelectedColor(primaryColor);

    // Set default language from company settings if user hasn't explicitly chosen one
    const SUPPORTED_LANGS = ["en", "fr", "es"];
    const userChosenLang = localStorage.getItem("userSelectedLang");
    if (!userChosenLang || !SUPPORTED_LANGS.includes(userChosenLang)) {
      try {
        const defaultLang =
          styleCustomization?.bookingFlowCustomizations?.defaultLanguage;
        if (defaultLang && SUPPORTED_LANGS.includes(defaultLang)) {
          i18n.changeLanguage(defaultLang);
        } else {
          i18n.changeLanguage("en");
        }
      } catch (e) {
        i18n.changeLanguage("en");
      }
    }
  };

  useEffect(() => {
    // get companyId passed in query string
    if (props && props.location && props.location.search) {
      params = parse(props.location.search);
      logger.debug("page = " + params.p); // "tippage"
      if (params.p && params.p !== "") {
        setLandingPage(true);
      }
    }

    async function doFetch() {
      await init();
    }
    doFetch();
  }, [selectedColor]);

  useEffect(() => {
    async function doFetch() {
      setLoggedOut(false);
      history.push("/");
      window.location.reload();
    }
    if (loggedOut) doFetch();
  }, [loggedOut]);

  function hideLoader() {
    const el = document.querySelector(".loader-container");
    if (el) {
      el.remove();
      setShowLoader(!showLoader);
    }
  }

  const historyListening = useRef(false);
  if (!historyListening.current) {
    historyListening.current = true;
    history.listen((newLocation, action) => {
      try {
        if (backPathingEnabled(newLocation)) {
          //In the case the backpathing should be enabled, return and do nothing.
          return;
        }

        if (action === "PUSH") {
          if (
            newLocation.pathname !== pathingInfo.currentPathname ||
            newLocation.search !== pathingInfo.currentSearch
          ) {
            pathingInfo.openMessage = false;
            state.bookingState.seenConfirmOrder = false;
            state.bookingState.seenOrderPayment = false;
            state.bookingState.seenBookingComplete = false;
            // Save new location
            pathingInfo.currentPathname = newLocation.pathname;
            pathingInfo.currentSearch = newLocation.search;
            // Clone location object and push it to history
            history.push({
              pathname: newLocation.pathname,
              search: newLocation.search,
            });
          }
        } else {
          // Send user back if they try to navigate back
          pathingInfo.openMessage = true;
          history.go(1);
        }
      } catch (e) {
        console.log("history listen error", e);
      }
    });
  }

  function backPathingEnabled(path) {
    if (
      path === "/" ||
      path === "/logout" ||
      path === "/login" ||
      path === "/notfound" ||
      path === "/msgtest" ||
      path === "/service"
    ) {
      return true;
    } else {
      return false;
    }
  }

  async function init() {
    // A/B test (single-host, see AbTest.js): the v2 cohort leaves before any
    // guest sign-in or data fetch; the static loader stays up until the
    // browser navigates away.
    if (await maybeRedirectToV2()) return;
    setLoading(false);
    state.bookingState = {};
    // log in as guest if not authenticated
    const restoredCustomer = await SignInAsGuest();
    attachCachedAbIdentity(
      restoredCustomer ? restoredCustomer.attributes?.email : null,
    );
    const { compId, company, isProviderAgnostic } =
      await getCompanyFromDomain();
    if (restoredCustomer && compId && isAbHost()) {
      // fire-and-forget; only queries on the single experiment host and
      // validates/refreshes the cached identity used above
      attachRestoredAbIdentity(restoredCustomer, compId);
    }
    const { offersRemoteServices, multipleServices, multipleQty } = company;
    if ((company && company.active === false) || !company) {
      history.push("/notfound");
      setLoading(true);
      hideLoader();
      return;
    }

    //Append MarketBox GTMId
    initializeGTM(process.env.REACT_APP_MB_GTM_ID);

    //route user to maintenance dialog if maintenance flag is ON
    if (process.env.REACT_APP_FF_ONGOING_MAINTENANCE === "true") {
      history.push("/maintenance");
    }
    const courseDatesQueryParameters = {
      companyId: compId,
      multipleServices,
      isProviderAgnostic,
    };
    setCourseDatesQueryParams(courseDatesQueryParameters);
    const [, , , , gtmIds, courseAssignments] = await Promise.all([
      fetchServiceTypeData(compId, multipleServices, isProviderAgnostic),
      fetchSkillsData(compId),
      fetchCompanyLocations(compId, offersRemoteServices),
      fetchCompanyPublicSettings(compId),
      fetchGTMIds(compId),
      fetchCourseDates(courseDatesQueryParameters),
    ]);
    setCourseDates(courseAssignments);
    if (gtmIds && Array.isArray(gtmIds) && gtmIds.length > 0) {
      initializeGTM(gtmIds[0]); //for now we will only embed one ID
    }

    setCompanyId(compId);
    state.bookingState.company = company;
    state.bookingState.companyId = compId;

    //check if company has a Tos and set 'hasTos' in bookingState
    const hasTos = await checkCompanyHasTosFile(compId);
    actions.setBookingState({
      ...state.bookingState,
      hasTos: hasTos,
    });

    setLoading(true);
    hideLoader();
  }

  function sortByOrdinal(services) {
    if (services) {
      return services.sort((s1, s2) => {
        const categoryDiff = s1.category.ordinal - s2.category.ordinal;
        if (categoryDiff !== 0) return categoryDiff;

        return s1.ordinal - s2.ordinal;
      });
    }
  }

  // Returns the Cognito user when an existing non-guest session was restored,
  // null otherwise.
  async function SignInAsGuest() {
    let userAuthenticated = false;
    let restoredCustomer = null;
    try {
      // check if user signed in
      try {
        const authenticatedUser = await Auth.currentAuthenticatedUser({
          bypassCache: true,
        });
        if (
          authenticatedUser &&
          authenticatedUser.signInUserSession &&
          authenticatedUser.signInUserSession.idToken &&
          authenticatedUser.signInUserSession.idToken.payload
        ) {
          let groups =
            authenticatedUser.signInUserSession.idToken.payload[
              "cognito:groups"
            ];
          if (groups && groups.length) {
            setUser(authenticatedUser);
            setAttributes(authenticatedUser.attributes);
            if (
              authenticatedUser.attributes.email ===
              process.env.REACT_APP_MB_GUEST_EMAIL
            ) {
              setGuestUser(true);
            } else {
              setGuestUser(false);
              restoredCustomer = authenticatedUser;
            }
            userAuthenticated = true;
            state.bookingState.user = authenticatedUser;
          }
        }
      } catch (e) {
        console.log("user not found", e.code);
      }

      if (!userAuthenticated) {
        const user = await Auth.signIn(
          process.env.REACT_APP_MB_GUEST_EMAIL,
          process.env.REACT_APP_MB_GUEST_PWD,
        );
        setUser(user);
        setAttributes(user.attributes);
        setGuestUser(true);

        state.bookingState.user = user;
      }
    } catch (err) {
      if (err.code === "UserNotConfirmedException") {
        // The error happens if the user didn't finish the confirmation step when signing up
        // In this case you need to resend the code and confirm the user
        // About how to resend the code and confirm the user, please check the signUp part
      } else if (err.code === "PasswordResetRequiredException") {
        // The error happens when the password is reset in the Cognito console
        // In this case you need to call forgotPassword to reset the password
        // Please check the Forgot Password part.
      } else if (err.code === "NotAuthorizedException") {
        // The error happens when the incorrect password is provided
      } else if (err.code === "UserNotFoundException") {
        // The error happens when the supplied username/email does not exist in the Cognito user pool
      } else {
        logger.debug(
          "An error occurred attempting to login. Error was: " +
            JSON.stringify(err),
        );
      }
    }
    return restoredCustomer;
  }

  // A signed-in customer restored from an existing session never passes
  // through SignIn, so their Statsig identity is re-attached here. No
  // login_successful event: a restore is not a sign-in.
  async function attachRestoredAbIdentity(restoredCustomer, compId) {
    try {
      const clientRecord = await getClientIfExists({
        userid: restoredCustomer.username,
        companyid: compId,
      });
      if (clientRecord && clientRecord.id) {
        setAbSignedInIdentity({
          clientId: clientRecord.id,
          email: restoredCustomer.attributes.email,
        });
      }
    } catch (e) {
      logger.debug("Unable to attach A/B identity on session restore", e);
    }
  }

  const updateCurrentUser = (u) => {
    state.bookingState.user = u;
    state.bookingState.bookingUser = u;
    actions.setBookingState(state.bookingState);
    setUser(u);
    setGuestUser(false);
  };

  const setBookingUserInState = (guest_mbuser) => {
    state.bookingState.bookingUser = guest_mbuser;
    state.bookingState.guestCheckout = true;
    setGuestCheckout(true);
  };

  const signOutOp = async () => {
    history.push("/logout");
  };

  const setClientInfoInBookingState = (clientInfoObj) => {
    state.bookingState.isNewClient = clientInfoObj.isNewClient;
    state.bookingState.client = clientInfoObj.client;
    actions.setBookingState(state.bookingState);
  };

  const setClientPackagesInBookingState = (clientPackages) => {
    state.bookingState.clientExistingPackages = clientPackages;
    actions.setBookingState(state.bookingState);
  };

  const getCompanyFromDomain = async () => {
    // enter domain here
    //frasiercrane-dev.gomarketbox.com
    let domain = window.location.hostname;
    //"justkeepswimming.gomarketbox.com";
    //let domain = "frasiercrane-dev.gomarketbox.com"; //"theebigrelease.gomarketbox.com"; //frasiercrane-dev.gomarketbox.com";
    let compId = "";
    let company = {};
    // fix to work for localhost
    if (domain === "localhost") {
      //domain = "gaming-dev.gomarketbox.com";
      domain = "frasiercrane.gomarketbox.com";
      //domain = "programmersyounite.gomarketbox.com";
      // domain = "jccs-devm.gomarketbox.com";
      // domain = "april21.gomarketbox.com";
    }
    const response = await API.graphql(
      graphqlOperation(companyBySubdomain, {
        subdomain: domain,
      }),
    );
    let isProviderAgnostic = false;
    if (
      response.data.companyBySubdomain.items &&
      response.data.companyBySubdomain.items.length === 1
    ) {
      compId = response.data.companyBySubdomain.items[0].id;
      company = response.data.companyBySubdomain.items[0];
      setCompany(company);
      setCompanyId(compId);
      isProviderAgnostic = providerAgnosticFlow(company);
      setDefaultCountry(company.countrycode3166alpha2 || "ca");
      localStorage.setItem("companyId", compId);
      localStorage.setItem("company", JSON.stringify(company));
    } else {
      logger.error(
        "*** ERROR ***: Unable to find a company for subdomain: " + domain,
      );
    }
    return { compId, company, isProviderAgnostic };
  };

  function providerAgnosticFlow(comp) {
    let isAgnostic = false;
    if (comp.ProvAgnoFlowConfig) {
      const ProvAgnoFlowConfig = JSON.parse(comp.ProvAgnoFlowConfig);
      setProvAgnoFlow(ProvAgnoFlowConfig.isEnabled);
      isAgnostic = ProvAgnoFlowConfig.isEnabled;
    }
    return isAgnostic;
  }

  function getCompanyTagline() {
    let tagline;
    if (company && company?.tagline?.length <= 0) {
      tagline = company.name;
    } else {
      tagline = company.tagline;
    }
    return tagline;
  }

  if (showLoader) {
    return null;
  }

  function FallbackComponent() {
    return (
      <div>
        We are sorry but an error has occurred while processing your action.
        Please submit the error report.
      </div>
    );
  }

  const myFallbackComponent = <FallbackComponent />;

  return (
    <>
      <Sentry.ErrorBoundary fallback={myFallbackComponent} showDialog>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <UserContext.Provider
              value={{ user, userAttributes, guestUser, guestCheckout }}
            >
              <div id="main-content" style={{ minHeight: "90vh" }}>
                <Helmet>
                  <title>
                    {company && company.active !== false
                      ? getCompanyTagline()
                      : "Company Not Found"}
                  </title>
                </Helmet>
                <Router history={history}>
                  <Route
                    path="/maintenance"
                    component={() => {
                      return loading ? (
                        <div>
                          <Suspense fallback={<div></div>}>
                            {process.env.REACT_APP_FF_ONGOING_MAINTENANCE ===
                            "true" ? (
                              <MaintenanceDialog />
                            ) : (
                              <SelectService
                                provAgnoFlow={provAgnoFlow}
                                serviceTypes={allActiveServices}
                                skills={skills}
                                companyLocations={companyLocations}
                                companyId={companyId}
                                company={company}
                                loading={serviceLoading}
                                publicsitesettings={publicSiteSettings}
                                signOutOp={signOutOp}
                                pathingInfo={pathingInfo}
                                selectedColor={
                                  selectedColor ? selectedColor : "#7AA0CD"
                                }
                              />
                            )}
                          </Suspense>
                        </div>
                      ) : (
                        ""
                      );
                    }}
                  />
                  <Route
                    path="/service"
                    component={() => {
                      return loading ? (
                        <Suspense fallback={<div></div>}>
                          <SelectService
                            provAgnoFlow={provAgnoFlow}
                            serviceTypes={allActiveServices}
                            skills={skills}
                            companyLocations={companyLocations}
                            companyId={companyId}
                            company={company}
                            loading={serviceLoading}
                            publicsitesettings={publicSiteSettings}
                            pathingInfo={pathingInfo}
                            signOutOp={signOutOp}
                            selectedColor={
                              selectedColor ? selectedColor : "#7AA0CD"
                            }
                          />
                        </Suspense>
                      ) : (
                        ""
                      );
                    }}
                  />
                  <Route
                    path="/contact"
                    component={() => {
                      return loading ? (
                        <Suspense fallback={<div></div>}>
                          <ContactPage
                            company={company}
                            publicsitesettings={publicSiteSettings}
                            pathingInfo={pathingInfo}
                            selectedColor={
                              selectedColor ? selectedColor : "#7AA0CD"
                            }
                          />
                        </Suspense>
                      ) : (
                        ""
                      );
                    }}
                  />
                  <Route
                    path="/notfound"
                    component={() => {
                      return loading ? (
                        <Suspense fallback={<div></div>}>
                          <NotFound
                            company={company}
                            pathingInfo={pathingInfo}
                            selectedColor={
                              selectedColor ? selectedColor : "#7AA0CD"
                            }
                          />
                        </Suspense>
                      ) : (
                        ""
                      );
                    }}
                  />
                  <Route
                    path="/pagenotfound"
                    component={() => {
                      return loading ? (
                        <Suspense fallback={<div></div>}>
                          <PageNotFound
                            company={company}
                            pathingInfo={pathingInfo}
                            selectedColor={
                              selectedColor ? selectedColor : "#7AA0CD"
                            }
                          />
                        </Suspense>
                      ) : (
                        ""
                      );
                    }}
                  />
                  <Route
                    exact
                    path="/providers"
                    component={() => {
                      return loading ? (
                        <Suspense fallback={<div></div>}>
                          <ProviderDirectory
                            serviceTypes={allServices}
                            skills={skills}
                            companyLocations={companyLocations}
                            companyId={companyId}
                            company={company}
                            loading={serviceLoading}
                            publicsitesettings={publicSiteSettings}
                            signOutOp={signOutOp}
                            pathingInfo={pathingInfo}
                            selectedColor={
                              selectedColor ? selectedColor : "#7AA0CD"
                            }
                          />
                        </Suspense>
                      ) : (
                        ""
                      );
                    }}
                  />
                  <Route
                    path="/providers/:providerName"
                    component={() => {
                      return loading ? (
                        <div>
                          <Suspense fallback={<div></div>}>
                            <ProviderPermalink
                              provAgnoFlow={provAgnoFlow}
                              skills={skills}
                              serviceTypes={allServices}
                              companyLocations={companyLocations}
                              companyId={companyId}
                              company={company}
                              publicSiteSettings={publicSiteSettings}
                              pathingInfo={pathingInfo}
                              signOutOp={signOutOp}
                              selectedColor={
                                selectedColor ? selectedColor : "#7AA0CD"
                              }
                            />
                          </Suspense>
                        </div>
                      ) : (
                        ""
                      );
                    }}
                  />
                  <Route
                    path="/provider/:providerName"
                    render={({ match }) => (
                      <Redirect
                        to={`/providers/${match.params.providerName}`}
                      />
                    )}
                  />
                  <Route
                    path="/choose-timeslot"
                    component={() => {
                      return loading ? (
                        <Suspense fallback={<div></div>}>
                          <ChooseTimeslot
                            serviceTypes={allServices}
                            skills={skills}
                            companyLocations={companyLocations}
                            companyId={companyId}
                            company={company}
                            loading={serviceLoading}
                            publicsitesettings={publicSiteSettings}
                            signOutOp={signOutOp}
                            pathingInfo={pathingInfo}
                            selectedColor={
                              selectedColor ? selectedColor : "#7AA0CD"
                            }
                          />
                        </Suspense>
                      ) : (
                        ""
                      );
                    }}
                  />
                  <Route
                    path="/auto-timeslot"
                    component={() => {
                      return loading ? (
                        <Suspense fallback={<div></div>}>
                          <AutoSelectProviderTimeSlot
                            serviceTypes={allServices}
                            skills={skills}
                            companyLocations={companyLocations}
                            companyId={companyId}
                            company={company}
                            loading={serviceLoading}
                            publicsitesettings={publicSiteSettings}
                            signOutOp={signOutOp}
                            pathingInfo={pathingInfo}
                            selectedColor={
                              selectedColor ? selectedColor : "#7AA0CD"
                            }
                          />
                        </Suspense>
                      ) : (
                        ""
                      );
                    }}
                  />
                  <Route
                    path="/confirmation"
                    component={() => {
                      return loading ? (
                        <Suspense fallback={<div></div>}>
                          <ConfirmAppointmentDetail
                            serviceTypes={allServices}
                            skills={skills}
                            companyLocations={companyLocations}
                            companyId={companyId}
                            company={company}
                            loading={serviceLoading}
                            publicsitesettings={publicSiteSettings}
                            signOutOp={signOutOp}
                            pathingInfo={pathingInfo}
                            selectedColor={
                              selectedColor ? selectedColor : "#7AA0CD"
                            }
                          />
                        </Suspense>
                      ) : (
                        ""
                      );
                    }}
                  />
                  <Route
                    path="/payment"
                    component={() => {
                      return loading ? (
                        <Suspense fallback={<div></div>}>
                          <OrderPayment
                            companyId={companyId}
                            company={company}
                            publicsitesettings={publicSiteSettings}
                            authuser={user}
                            signOutOp={signOutOp}
                            pathingInfo={pathingInfo}
                            selectedColor={
                              selectedColor ? selectedColor : "#7AA0CD"
                            }
                          />
                        </Suspense>
                      ) : (
                        ""
                      );
                    }}
                  />
                  <Route
                    path="/confirm-order"
                    component={() => {
                      return loading ? (
                        <Suspense fallback={<div></div>}>
                          <ConfirmOrder
                            serviceTypes={allServices}
                            skills={skills}
                            companyLocations={companyLocations}
                            companyId={companyId}
                            company={company}
                            loading={serviceLoading}
                            publicsitesettings={publicSiteSettings}
                            signOutOp={signOutOp}
                            pathingInfo={pathingInfo}
                            selectedColor={
                              selectedColor ? selectedColor : "#7AA0CD"
                            }
                          />
                        </Suspense>
                      ) : (
                        ""
                      );
                    }}
                  />
                  <Route
                    path="/choose-package"
                    component={() => {
                      return loading ? (
                        <Suspense fallback={<div></div>}>
                          <ChoosePackage
                            serviceTypes={allServices}
                            skills={skills}
                            companyLocations={companyLocations}
                            companyId={companyId}
                            company={company}
                            loading={serviceLoading}
                            publicsitesettings={publicSiteSettings}
                            pathingInfo={pathingInfo}
                            signOutOp={signOutOp}
                            selectedColor={
                              selectedColor ? selectedColor : "#7AA0CD"
                            }
                          />
                        </Suspense>
                      ) : (
                        ""
                      );
                    }}
                  />
                  <Route
                    path="/booking-complete"
                    component={() => {
                      return loading ? (
                        <Suspense fallback={<div></div>}>
                          <BookingComplete
                            companyId={companyId}
                            company={company}
                            loading={serviceLoading}
                            signOutOp={signOutOp}
                            publicsitesettings={publicSiteSettings}
                            pathingInfo={pathingInfo}
                            selectedColor={
                              selectedColor ? selectedColor : "#7AA0CD"
                            }
                          />
                        </Suspense>
                      ) : (
                        ""
                      );
                    }}
                  />
                  <Route
                    path="/logout"
                    component={() => {
                      return loading ? (
                        <Suspense fallback={<div></div>}>
                          <Logout
                            companyId={companyId}
                            company={company}
                            loading={serviceLoading}
                            setLoggedOut={setLoggedOut}
                            publicsitesettings={publicSiteSettings}
                            pathingInfo={pathingInfo}
                            selectedColor={
                              selectedColor ? selectedColor : "#7AA0CD"
                            }
                          />{" "}
                        </Suspense>
                      ) : (
                        ""
                      );
                    }}
                  />
                  <Route
                    path="/login"
                    component={() => {
                      return loading ? (
                        <div>
                          <SignIn
                            companyId={companyId}
                            company={company}
                            setUser={updateCurrentUser}
                            setClient={setClientInfoInBookingState}
                            setClientPackages={setClientPackagesInBookingState}
                            publicsitesettings={publicSiteSettings}
                            appPath={currentPath}
                            pathingInfo={pathingInfo}
                            selectedColor={
                              selectedColor ? selectedColor : "#7AA0CD"
                            }
                          />
                        </div>
                      ) : (
                        ""
                      );
                    }}
                  />
                  <Route
                    path="/signup"
                    component={() => {
                      return loading && defaultCountry ? (
                        company?.clientMayCreateAccount ? (
                          <div>
                            <SignUp
                              companyId={companyId}
                              company={company}
                              defaultCountry={defaultCountry}
                              setBookingUserInState={setBookingUserInState}
                              setUser={updateCurrentUser}
                              setClient={setClientInfoInBookingState}
                              setClientPackages={
                                setClientPackagesInBookingState
                              }
                              publicsitesettings={publicSiteSettings}
                              appPath={currentPath}
                              pathingInfo={pathingInfo}
                              selectedColor={
                                selectedColor ? selectedColor : "#7AA0CD"
                              }
                            />
                          </div>
                        ) : (
                          <Redirect to="/" />
                        )
                      ) : (
                        <></>
                      );
                    }}
                  />
                  <Route
                    path="/forgot"
                    component={() => {
                      return loading ? (
                        <div>
                          <Forgot
                            companyId={companyId}
                            company={company}
                            setUser={updateCurrentUser}
                            setClient={setClientInfoInBookingState}
                            setClientPackages={setClientPackagesInBookingState}
                            publicsitesettings={publicSiteSettings}
                            appPath={currentPath}
                            pathingInfo={pathingInfo}
                            selectedColor={
                              selectedColor ? selectedColor : "#7AA0CD"
                            }
                          />
                        </div>
                      ) : (
                        ""
                      );
                    }}
                  />
                  <Route
                    path="/msgtest"
                    component={() => {
                      return loading ? (
                        <div>
                          <Suspense fallback={<div></div>}>
                            <MsgTest
                              companyLocations={companyLocations}
                              companyId={companyId}
                              company={company}
                              loading={serviceLoading}
                              publicsitesettings={publicSiteSettings}
                              signOutOp={signOutOp}
                              pathingInfo={pathingInfo}
                              selectedColor={
                                selectedColor ? selectedColor : "#7AA0CD"
                              }
                            ></MsgTest>
                          </Suspense>
                        </div>
                      ) : (
                        ""
                      );
                    }}
                  />
                  <Route
                    exact
                    path="/"
                    component={() => {
                      if (params && params.p && params.p === "tippage") {
                        return loading ? (
                          <TipPage
                            companyId={companyId}
                            company={company}
                            authuser={user}
                            selectedColor={
                              selectedColor ? selectedColor : "#7AA0CD"
                            }
                          />
                        ) : (
                          ""
                        );
                      } else {
                        return loading ? (
                          <Suspense fallback={<div></div>}>
                            <SelectService
                              provAgnoFlow={provAgnoFlow}
                              serviceTypes={allActiveServices}
                              skills={skills}
                              companyLocations={companyLocations}
                              companyId={companyId}
                              company={company}
                              loading={serviceLoading}
                              publicsitesettings={publicSiteSettings}
                              signOutOp={signOutOp}
                              pathingInfo={pathingInfo}
                              selectedColor={
                                selectedColor ? selectedColor : "#7AA0CD"
                              }
                            />
                          </Suspense>
                        ) : (
                          ""
                        );
                      }
                    }}
                  />
                </Router>
              </div>
              <div className="footer">
                <Typography variant="subtitle2">Powered by</Typography>
                <img
                  src={mbLogo}
                  alt="MarketBox"
                  style={{
                    width: "90px",
                    marginBottom: "10px",
                  }}
                />
              </div>
            </UserContext.Provider>
          </ThemeProvider>
        </StyledEngineProvider>
      </Sentry.ErrorBoundary>
    </>
  );
}

export default Sentry.withProfiler(withRouter(App));
